import { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Loader2, FileText, ImageIcon } from 'lucide-react';

import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import { Form } from '@/components/molecules/form-field';
import { FormPageHeader, FormProgressIndicator } from '@/components/forms';

import {
  ProductBasicInfo,
  ProductPricing,
  ProductAttributes,
  ProductWeight,
  ProductTags,
  ProductImages,
  useProduct,
  useUpdateProduct,
  useUpdateProductImages,
  useProductImages,
  useProductEarnings,
  updateProductSchema,
  type UpdateProductFormData,
} from '@/features/products';

import { useCategories } from '@/lib/hooks/use-categories';
import { useCategoryRequirements } from '@/lib/hooks/use-category-requirements';
import { getImageUrl, parseStringArray } from '@/lib/utils';
import type { Product, ImageOrderData } from '@/lib/types';

// Extended image preview type for tracking
interface ImagePreviewData {
  src: string;
  id: number | string;
  isNew?: boolean;
  order: number;
  name?: string;
  url?: string;
}

export default function EditProductPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const { data: product, isLoading: isLoadingProduct } = useProduct(id ?? '');
  const { data: categories, isLoading: isLoadingCategories } = useCategories();
  const { mutate: updateProduct, isPending: isSubmitting } = useUpdateProduct();
  const { mutate: updateImages, isPending: isUpdatingImages } = useUpdateProductImages();

  // Image handling for new images with cropping support
  const {
    images: newImages,
    imagePreviews: newImagePreviews,
    isCompressing,
    handleAddImages,
    handleRemoveImage: handleRemoveNewImage,
    resetImages,
    pendingCropImage,
    cropQueue,
    handleCropComplete,
    handleCropCancel,
  } = useProductImages({ enableCropping: true });

  // Track all images in order (existing + new combined)
  const [allImagesOrder, setAllImagesOrder] = useState<ImagePreviewData[]>([]);
  const [isImagesInitialized, setIsImagesInitialized] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<UpdateProductFormData['currency']>('european_currency');

  // Initialize images from product
  useEffect(() => {
    if (product?.images && !isImagesInitialized) {
      const existingImages: ImagePreviewData[] = product.images.map((img, index) => {
        const imageObj = typeof img === 'object' ? img : null;
        return {
          src: getImageUrl(img) || '',
          id: imageObj?.id ?? `existing-${index}`,
          isNew: false,
          order: imageObj?.order ?? index,
          name: imageObj?.name || `image-${index}.jpg`,
          url: getImageUrl(img) || '',
        };
      }).filter(img => img.src);

      // Sort images by order to ensure correct display
      existingImages.sort((a, b) => Number(a.order) - Number(b.order));

      // Recalculate sequential orders after sorting
      const sortedImages = existingImages.map((img, idx) => ({ ...img, order: idx }));

      setAllImagesOrder(sortedImages);
      setIsImagesInitialized(true);
    }
  }, [product?.images, isImagesInitialized]);

  // Sync new images to allImagesOrder (only add new ones, preserve existing order)
  useEffect(() => {
    if (!isImagesInitialized) return;

    setAllImagesOrder(prev => {
      // Find which new images need to be added (not already in allImagesOrder)
      const newImagesToAdd: ImagePreviewData[] = [];
      newImagePreviews.forEach((src, index) => {
        const newId = `new-${index}`;
        // Check if this new image already exists in allImagesOrder
        const existsInOrder = prev.some(img => img.isNew && img.src === src);
        if (!existsInOrder) {
          newImagesToAdd.push({
            src,
            id: newId,
            isNew: true,
            order: prev.length + newImagesToAdd.length,
            name: newImages[index]?.name || `new-image-${index}.jpg`,
          });
        }
      });

      // If there are new images to add, append them
      if (newImagesToAdd.length > 0) {
        const updated = [...prev, ...newImagesToAdd];
        // Recalculate orders
        return updated.map((img, idx) => ({ ...img, order: idx }));
      }

      // If new images were removed (newImagePreviews is shorter), filter them out
      const newImageSrcs = new Set(newImagePreviews);
      const filtered = prev.filter(img => {
        if (!img.isNew) return true; // Keep existing images
        return newImageSrcs.has(img.src); // Keep only new images that still exist
      });

      if (filtered.length !== prev.length) {
        // Recalculate orders
        return filtered.map((img, idx) => ({ ...img, order: idx }));
      }

      return prev;
    });
  }, [newImagePreviews, newImages, isImagesInitialized]);

  // Parse prices from product
  const parsePrices = (prices?: Product['prices']) => {
    if (!prices) return { euro: 0, comoros: 0, usd: 0 };
    return {
      euro: typeof prices.euro === 'string' ? parseFloat(prices.euro) : (prices.euro || 0),
      comoros: typeof prices.comoros === 'string' ? parseFloat(prices.comoros) : (prices.comoros || 0),
      usd: typeof prices.usd === 'string' ? parseFloat(prices.usd) : (prices.usd || 0),
    };
  };

  // Compute initial form values
  const initialFormValues = useMemo((): UpdateProductFormData | undefined => {
    if (!product) return undefined;

    let categoryId = '';
    if (product.categories?.length) categoryId = String(product.categories[0].id || '');
    else if (product.categoryId) categoryId = String(product.categoryId);

    const sizesStr = Array.isArray(product.sizes) ? product.sizes.join(', ') : (product.sizes || '');
    const tagsStr = Array.isArray(product.tags) ? product.tags.join(', ') : (product.tags || '');
    const quantity = typeof product.stock === 'number' ? product.stock : parseInt(String(product.stock) || '0', 10) || 0;
    const weight = typeof product.weight === 'number' && product.weight > 0 ? product.weight : parseFloat(String(product.weight) || '0') || 0;

    const prices = parsePrices(product.prices);
    const priceMap: Record<UpdateProductFormData['currency'], number> = {
      'european_currency': prices.euro,
      'comoros_currency': prices.comoros,
      'american_currency': prices.usd,
    };

    return {
      categoryId,
      name: product.name,
      description: product.description || '',
      price: priceMap[selectedCurrency] || prices.euro || 0,
      quantity,
      currency: selectedCurrency,
      unit: (product.unit as UpdateProductFormData['unit']) || 'sold_individually',
      gender: (product.gender as UpdateProductFormData['gender']) || undefined,
      sizes: sizesStr,
      tags: tagsStr,
      weight,
      weightUnit: (product.weight_unit as UpdateProductFormData['weightUnit']) || 'grams',
    };
  }, [product, selectedCurrency]);

  const form = useForm<UpdateProductFormData>({
    resolver: zodResolver(updateProductSchema),
    defaultValues: {
      categoryId: '',
      name: '',
      description: '',
      price: 0,
      quantity: 0,
      currency: 'european_currency',
      unit: 'sold_individually',
      gender: undefined,
      sizes: '',
      tags: '',
      weight: 0,
      weightUnit: 'grams',
    },
    values: initialFormValues,
    resetOptions: { keepDirtyValues: true },
  });

  const { dirtyFields } = form.formState;

  // Check for changes in information fields (excluding images)
  const hasInformationChanges = Object.keys(dirtyFields).some(
    (key) => key !== 'images' && Boolean(dirtyFields[key as keyof typeof dirtyFields])
  );

  // Check for image changes
  const originalImagesCount = product?.images?.length ?? 0;
  const existingImagesInOrder = allImagesOrder.filter(img => !img.isNew);
  const newImagesInOrder = allImagesOrder.filter(img => img.isNew);

  // Check if order changed by comparing original order with current
  const hasOrderChanged = useMemo(() => {
    if (!product?.images) return false;
    const currentExistingIds = existingImagesInOrder.map(img => img.id);
    const originalIds = product.images.map((img, idx) =>
      typeof img === 'object' && 'id' in img ? img.id : `existing-${idx}`
    );

    // Check if order or presence changed
    if (currentExistingIds.length !== originalIds.length) return true;
    return currentExistingIds.some((id, idx) => id !== originalIds[idx]);
  }, [product?.images, existingImagesInOrder]);

  const hasImageChanges = newImagesInOrder.length > 0 ||
    existingImagesInOrder.length !== originalImagesCount ||
    hasOrderChanged;

  // Currency change handler
  const handleCurrencyChange = (newCurrency: UpdateProductFormData['currency']) => {
    setSelectedCurrency(newCurrency);
    if (product?.prices) {
      const prices = parsePrices(product.prices);
      const priceMap: Record<UpdateProductFormData['currency'], number> = {
        'european_currency': prices.euro,
        'comoros_currency': prices.comoros,
        'american_currency': prices.usd,
      };
      if (priceMap[newCurrency] > 0) {
        form.setValue('price', priceMap[newCurrency], { shouldDirty: false });
      }
    }
  };

  // Category requirements
  const watchCategory = form.watch('categoryId');
  const selectedCategory = categories?.find((c) => String(c.id) === watchCategory);
  const requirements = useCategoryRequirements(selectedCategory?.name);
  const hasExistingSizes = Array.isArray(product?.sizes) && product.sizes.length > 0;

  const formValues = form.watch();

  // Earnings estimation
  const earningsEstimation = useProductEarnings({ price: formValues.price });

  // Form progress
  const imageCount = allImagesOrder.length;
  const { completedFields, totalFields } = useMemo(() => {
    const requiredFields = ['categoryId', 'name', 'description', 'price', 'quantity', 'currency', 'unit', 'weight', 'weightUnit'];
    if (requirements.showGender) requiredFields.push('gender');

    let completed = requiredFields.filter((field) => {
      const value = formValues[field as keyof UpdateProductFormData];
      if (field === 'weight') return typeof value === 'number' && value > 0;
      return value !== '' && value !== undefined && value !== 0;
    }).length;

    if (imageCount > 0) completed++;
    const total = requiredFields.length + 1;

    return { completedFields: completed, totalFields: total };
  }, [formValues, requirements, imageCount]);

  // Handle image removal
  const handleImageRemove = useCallback((_index: number, id?: number | string) => {
    if (id === undefined) return;

    // Find the image in allImagesOrder to get its src
    const imageToRemove = allImagesOrder.find(img => img.id === id);

    // Check if it's a new image - find it by src in newImagePreviews
    if (imageToRemove?.isNew && imageToRemove.src) {
      const newIndex = newImagePreviews.findIndex(src => src === imageToRemove.src);
      if (newIndex !== -1) {
        handleRemoveNewImage(newIndex);
      }
    }

    // Remove from allImagesOrder
    setAllImagesOrder(prev => {
      const filtered = prev.filter(img => img.id !== id);
      // Recalculate orders
      return filtered.map((img, idx) => ({ ...img, order: idx }));
    });
  }, [handleRemoveNewImage, allImagesOrder, newImagePreviews]);

  // Handle image reorder (for drag & drop)
  const handleImageReorder = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    setAllImagesOrder(prev => {
      const newOrder = [...prev];
      const [movedItem] = newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, movedItem);
      // Recalculate orders
      return newOrder.map((img, idx) => ({ ...img, order: idx }));
    });
  }, []);

  // Submit information only
  const onSubmitInformation = async (data: UpdateProductFormData) => {
    if (!product) return;

    const sizesArray = parseStringArray(data.sizes);
    const shopRefId = product.shop?.refId || product.shop?.id || product.shopId || '';
    const productRefId = product.refId || product.id;

    updateProduct({
      productRefId,
      shopRefId,
      data: {
        name: data.name,
        description: data.description || '',
        price: data.price,
        quantity: data.quantity,
        currency: data.currency,
        unit: data.unit,
        gender: data.gender || 'none',
        sizes: sizesArray,
        categories: data.categoryId ? [parseInt(data.categoryId)] : [],
        tags: data.tags || '',
        weight: data.weight ?? undefined,
        weight_unit: data.weightUnit ?? undefined,
      },
    }, {
      onSuccess: () => {
        toast.success(t('products.infoUpdated', { defaultValue: 'Information updated successfully' }));
        form.reset(data);
      },
      onError: () => {
        toast.error(t('products.infoUpdateFailed', { defaultValue: 'Failed to update information' }));
      },
    });
  };

  // Submit images only - using orders format like mobile-pro
  const onSubmitImages = useCallback(() => {
    if (!product) return;

    if (allImagesOrder.length === 0) {
      toast.error(t('products.imagesRequired'));
      return;
    }

    // Build orders array (matching mobile-pro format)
    const orders: ImageOrderData[] = allImagesOrder.map((img, index) => ({
      url: img.isNew ? 'file' : (img.url || img.src),
      order: index,
      isFile: img.isNew ?? false,
      name: img.name || `image-${index}.jpg`,
      id: String(img.id),
    }));

    // Get only the new image files to upload
    const filesToUpload = newImages;

    const productRefId = product.refId || product.id;

    updateImages(
      { productRefId, images: filesToUpload, orders },
      {
        onSuccess: () => {
          toast.success(t('products.imagesUpdated', { defaultValue: 'Images updated successfully' }));
          // Reset new images state
          resetImages();
          // Reset initialization to refresh from server
          setIsImagesInitialized(false);
        },
        onError: () => {
          toast.error(t('products.imagesUpdateFailed', { defaultValue: 'Failed to update images' }));
        },
      }
    );
  }, [product, allImagesOrder, newImages, updateImages, t, resetImages]);

  if (isLoadingProduct) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-9 w-9" />
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('products.error')}</p>
      </div>
    );
  }

  // Build image previews for display
  const imagePreviewsData = allImagesOrder.map(img => ({
    src: img.src,
    id: img.id,
    isNew: img.isNew,
  })).filter(p => p.src);

  return (
    <div className="pb-6">
      <FormPageHeader
        backLink={`/products/${id}`}
        title={t('products.editProduct')}
        description={product.name}
      />

      <FormProgressIndicator
        completed={completedFields}
        total={totalFields}
        label={t('products.fieldsCompleted')}
      />

      <Form {...form}>
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="flex flex-col gap-6 lg:grid lg:grid-cols-3">
            {/* Images - first on mobile */}
            <div className="order-first lg:order-last space-y-6">
              <ProductImages
                previews={imagePreviewsData}
                onAdd={handleAddImages}
                onRemove={handleImageRemove}
                onReorder={handleImageReorder}
                isCompressing={isCompressing}
                pendingCropImage={pendingCropImage}
                cropQueueLength={cropQueue.length}
                onCropComplete={handleCropComplete}
                onCropCancel={handleCropCancel}
              />

              {/* Desktop buttons */}
              <div className="hidden lg:flex lg:flex-col gap-2 sticky bottom-4">
                <Button
                  type="button"
                  className="w-full"
                  size="lg"
                  disabled={isSubmitting || !hasInformationChanges}
                  onClick={form.handleSubmit(onSubmitInformation)}
                >
                  {isSubmitting && <Loader2 className="size-4 mr-2 animate-spin" />}
                  {!isSubmitting && <FileText className="size-4 mr-2" />}
                  {t('products.updateInformation', { defaultValue: 'Update Information' })}
                </Button>
                <Button
                  type="button"
                  className="w-full"
                  size="lg"
                  variant="secondary"
                  disabled={isUpdatingImages || !hasImageChanges || isCompressing}
                  onClick={onSubmitImages}
                >
                  {isUpdatingImages && <Loader2 className="size-4 mr-2 animate-spin" />}
                  {!isUpdatingImages && <ImageIcon className="size-4 mr-2" />}
                  {t('products.updateImages', { defaultValue: 'Update Images' })}
                </Button>
              </div>
            </div>

            {/* Form fields */}
            <div className="lg:col-span-2 space-y-6">
              <ProductBasicInfo
                form={form}
                categories={categories}
                isLoadingCategories={isLoadingCategories}
              />

              <ProductPricing
                form={form}
                earningsEstimation={earningsEstimation}
                onCurrencyChange={handleCurrencyChange}
              />

              <ProductAttributes
                form={form}
                showGender={requirements.showGender}
                showSizes={requirements.showSizes || hasExistingSizes}
              />

              <ProductWeight form={form} useBasicUnits />

              <ProductTags form={form} />

              {/* Mobile buttons */}
              <div className="lg:hidden space-y-2">
                <Button
                  type="button"
                  className="w-full"
                  size="lg"
                  disabled={isSubmitting || !hasInformationChanges}
                  onClick={form.handleSubmit(onSubmitInformation)}
                >
                  {isSubmitting && <Loader2 className="size-4 mr-2 animate-spin" />}
                  {!isSubmitting && <FileText className="size-4 mr-2" />}
                  {t('products.updateInformation', { defaultValue: 'Update Information' })}
                </Button>
                <Button
                  type="button"
                  className="w-full"
                  size="lg"
                  variant="secondary"
                  disabled={isUpdatingImages || !hasImageChanges || isCompressing}
                  onClick={onSubmitImages}
                >
                  {isUpdatingImages && <Loader2 className="size-4 mr-2 animate-spin" />}
                  {!isUpdatingImages && <ImageIcon className="size-4 mr-2" />}
                  {t('products.updateImages', { defaultValue: 'Update Images' })}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
